import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Badge } from "@/components/common/DataTable";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { formatCurrency } from "@/lib/currency";

export default function PlansPage() {
  const { hasPermission } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setPlans((await api.get("/v1/plans")).data.data); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const publish = async (p) => {
    try { await api.patch(`/v1/plans/${p.id}/publish`); toast.success("Plan published"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="plans-page">
      <PageHeader title="Subscription Plans" subtitle="Database-driven commercial plans (nothing hardcoded)" />
      {loading ? <div className="text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading…</div> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-testid={`plan-${p.planCode}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs font-semibold text-slate-400">{p.planCode}</span>
                {p.isPublished ? <Badge color="green">Published</Badge> : <Badge color="amber">Draft</Badge>}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mt-2">{p.planName}</h3>
              <div className="mt-2">
                <span className="text-2xl font-extrabold text-primary">
                  {p.pricing?.custom ? "Custom" : formatCurrency(p.pricing?.amount || 0)}
                </span>
                {!p.pricing?.custom && <span className="text-sm text-slate-400">/{(p.pricing?.billingCycle || "mo").toLowerCase()}</span>}
              </div>
              <p className="text-sm text-slate-500 mt-2 min-h-[40px]">{p.description}</p>
              <div className="mt-3 space-y-1.5 flex-1">
                <div className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="h-4 w-4 text-primary" /> {(p.includedModuleCodes || []).length} modules</div>
                <div className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="h-4 w-4 text-primary" /> {(p.includedFeatureCodes || []).length} features</div>
                <div className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="h-4 w-4 text-primary" /> {p.maxUsers === null ? "Unlimited" : p.maxUsers} users</div>
              </div>
              {hasPermission("PLAN.PUBLISH") && !p.isPublished && (
                <button onClick={() => publish(p)} className="mt-4 w-full bg-primary text-white rounded-lg py-2 text-sm font-semibold hover:bg-primary/90" data-testid={`publish-${p.planCode}`}>Publish</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
